import React from "react";
import { RollbackOutlined, UploadOutlined } from "@ant-design/icons";
import { ConfigProvider, Input as AntInput } from "antd";
import type { FormInstance } from "antd";
import {
  Button,
  Form,
  Input,
  Select,
} from "../../../../components/shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { BannerIn } from "../types";
import styles from "../stylesDynamicBonuses.module.css";
import BannerDeeplinkField from "./BannerDeeplinkField";
import { ConfiguredBannerPreview } from "./BannerDesignField";
import CustomizeBannerModal from "./CustomizeBannerModal";
import CustomizeBannerPaletteIcon from "./CustomizeBannerPaletteIcon";
import UploadImageModal from "./UploadImageModal";
import {
  applyBannerThemePreset,
  bannerThemePreset,
  getBannerThemeOptions,
  type BannerTheme,
} from "./bannerThemes";

type NamePath = (string | number)[];

const httpUrlRule = {
  validator: (_: unknown, value?: string) => {
    if (!value || value.startsWith("http://") || value.startsWith("https://")) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error(AppStrings.PAM_DYNAMIC_BONUS_BANNER_HTTP_URL_RULE),
    );
  },
};

const colorsEqual = (a?: string[] | null, b?: string[] | null): boolean => {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  return left.every(
    (color, index) => color.toUpperCase() === right[index]?.toUpperCase(),
  );
};

const colorEqual = (a?: string | null, b?: string | null): boolean =>
  (a ?? "").toUpperCase() === (b ?? "").toUpperCase();

/** True when banner palette fields still match a theme preset (UI Custom state). */
export const bannerMatchesThemePreset = (
  banner: BannerIn | undefined,
  theme: BannerTheme,
): boolean => {
  const preset = bannerThemePreset(theme);
  return (
    colorsEqual(banner?.background_color, preset.backgroundColors) &&
    colorsEqual(banner?.border_color, preset.borderColors) &&
    colorEqual(banner?.title?.color, preset.titleColor) &&
    colorEqual(banner?.short_description?.color, preset.shortDescriptionColor) &&
    colorEqual(banner?.long_description?.color, preset.longDescriptionColor) &&
    colorEqual(banner?.details?.color, preset.detailsColor) &&
    colorEqual(banner?.timer?.background_color, preset.timerBackgroundColor) &&
    colorEqual(banner?.timer?.text_color, preset.timerTextColor) &&
    colorEqual(banner?.timer?.icon_color, preset.timerIconColor) &&
    colorEqual(banner?.timer?.border_color, preset.timerBorderColor) &&
    colorEqual(banner?.claim?.text_color, preset.claimTextColor) &&
    colorsEqual(banner?.claim?.background_color, preset.claimBackgroundColors) &&
    colorEqual(banner?.claim?.border_color, preset.claimBorderColor)
  );
};

type Props = {
  form: FormInstance;
  namePath: NamePath;
  itemNamePath: NamePath;
  /**
   * When true (default), title + short description are required — used for
   * OFFER banners and the campaign_start step-1 award receipt. Follow-up /
   * event awards can author a banner optionally.
   */
  requireCopy?: boolean;
  /** Optional content rendered below the live preview (e.g. display locations). */
  belowPreview?: React.ReactNode;
  /** Keeps image upload / customize modals usable when the campaign form is disabled. */
  enableWhenFormDisabled?: boolean;
  resultParamsPath?: NamePath;
  conditionsPath?: NamePath;
  restrictionDisplayNames?: Record<string, string>;
  /**
   * Render-prop escape hatch (BONENG-874): when provided, this component
   * renders no layout of its own — it hands the caller its banner form
   * fields and live-preview column so they can be composed into a single
   * grid alongside other step content (trigger/result/conditions) instead of
   * sitting in a second grid below it. State (theme, customization, upload
   * modal) still lives here regardless of which mode is used.
   */
  children?: (parts: {
    formFields: React.ReactNode;
    previewColumn: React.ReactNode;
  }) => React.ReactNode;
};

const StepBannerEditor: React.FC<Props> = ({
  form,
  namePath,
  itemNamePath,
  requireCopy = true,
  belowPreview,
  enableWhenFormDisabled = false,
  resultParamsPath,
  conditionsPath,
  restrictionDisplayNames,
  children,
}) => {
  const itemPath = (...segments: NamePath): NamePath => [
    ...itemNamePath,
    ...segments,
  ];
  const banner =
    (Form.useWatch(namePath, form) as BannerIn | undefined) ?? {};
  const [appliedTheme, setAppliedTheme] = React.useState<BannerTheme>("brand");
  const [isCustomizeOpen, setIsCustomizeOpen] = React.useState(false);
  const [isUploadOpen, setIsUploadOpen] = React.useState(false);

  const detailsTextPath = React.useMemo(
    () => [...namePath, "details", "text", "en"] as NamePath,
    // namePath is rebuilt each render; stabilize on its primitive segments.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- join encodes path identity
    [namePath.join(".")],
  );

  // Details copy is a shared TCM string (BONENG-807), not free text — keep the
  // form value locked even when editing older banners that stored custom labels.
  React.useEffect(() => {
    form.setFieldValue(detailsTextPath, AppStrings.DETAILS);
  }, [form, detailsTextPath]);

  const selectedTheme: BannerTheme | "custom" = bannerMatchesThemePreset(
    banner,
    appliedTheme,
  )
    ? appliedTheme
    : "custom";

  const applyTheme = (theme: BannerTheme) => {
    form.setFieldValue(namePath, applyBannerThemePreset(banner, theme));
    setAppliedTheme(theme);
  };

  const bannerPreviewBlock = (
    <div className={styles.bannerDesignPreview}>
      <div className={styles.bannerThemeControls}>
        <div className={styles.bannerThemeLabelRow}>
          <span className={styles.bannerThemeLabel}>
            {AppStrings.PAM_DYNAMIC_BONUS_BANNER_SELECT_THEME}
          </span>
          <Button
            htmlType="button"
            intent="neutral"
            variant="ghost"
            className={styles.displayTextAction}
            iconLeft={<CustomizeBannerPaletteIcon />}
            onClick={() => setIsCustomizeOpen(true)}
          >
            {AppStrings.PAM_DYNAMIC_BONUS_BANNER_CUSTOMIZE}
          </Button>
        </div>
        <Form.Item>
          <Select
            value={appliedTheme}
            options={getBannerThemeOptions()}
            onChange={applyTheme}
            aria-label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_SELECT_THEME}
          />
        </Form.Item>
      </div>
      <p className={styles.bannerDesignPreviewLabel}>
        {AppStrings.PAM_DYNAMIC_BONUS_BANNER_PREVIEW_LABEL}
      </p>
      <ConfiguredBannerPreview banner={banner} />
    </div>
  );

  const previewColumn = (
    <div
      className={`${styles.bannerEditorColumn} ${styles.bannerEditorPreviewColumn}`}
    >
      {bannerPreviewBlock}
      {belowPreview}
    </div>
  );

  const bannerFormFields = (
    <>
      <section className={styles.bannerEditorSection}>
        <Form.Item
          label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_LABEL}
          name={itemPath("title", "text", "en")}
          rules={
            requireCopy
              ? [
                  {
                    required: true,
                    whitespace: true,
                    message: AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_REQUIRED,
                  },
                ]
              : undefined
          }
        >
          <Input
            placeholder={AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_PLACEHOLDER}
          />
        </Form.Item>
      </section>

      <section className={styles.bannerEditorSection}>
        <Form.Item
          label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_DESCRIPTION_LABEL}
          name={itemPath("short_description", "text", "en")}
          rules={
            requireCopy
              ? [
                  {
                    required: true,
                    whitespace: true,
                    message:
                      AppStrings.PAM_DYNAMIC_BONUS_BANNER_DESCRIPTION_REQUIRED,
                  },
                ]
              : undefined
          }
        >
          <AntInput.TextArea
            autoSize={{ minRows: 2 }}
            placeholder={
              AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_PLACEHOLDER
            }
          />
        </Form.Item>
      </section>

      <section className={styles.bannerEditorSection}>
        <Form.Item
          label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_FULL_DESCRIPTION_LABEL}
          name={itemPath("long_description", "text", "en")}
        >
          <AntInput.TextArea
            autoSize={{ minRows: 3 }}
            placeholder={
              AppStrings.PAM_DYNAMIC_BONUS_BANNER_TITLE_PLACEHOLDER
            }
          />
        </Form.Item>
      </section>

      <section className={styles.bannerEditorSection}>
        <div className={styles.formFieldRow}>
          <div className={styles.uploadImageFieldWrap}>
            <div className={styles.bannerImageLabelRow}>
              <span className={styles.bannerImageLabelText}>
                {AppStrings.PAM_DYNAMIC_BONUS_BANNER_IMAGE_LABEL}
              </span>
              <span className={styles.bannerImageActions}>
                {banner.image?.url ? (
                  <button
                    type="button"
                    className={styles.resetImageTrigger}
                    onClick={() =>
                      form.setFieldValue(
                        [...namePath, "image", "url"],
                        undefined,
                      )
                    }
                    data-testid="banner-reset-image-button"
                  >
                    <RollbackOutlined />{" "}
                    {AppStrings.DYNAMIC_BONUS_BANNER_RESET_IMAGE}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.uploadImageTrigger}
                  onClick={() => setIsUploadOpen(true)}
                  data-testid="banner-upload-image-button"
                >
                  <UploadOutlined />{" "}
                  {AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_IMAGE}
                </button>
              </span>
            </div>
            <Form.Item name={itemPath("image", "url")} rules={[httpUrlRule]}>
              <Input
                aria-label={AppStrings.PAM_DYNAMIC_BONUS_BANNER_IMAGE_LABEL}
                placeholder={
                  AppStrings.PAM_DYNAMIC_BONUS_BANNER_IMAGE_PLACEHOLDER
                }
              />
            </Form.Item>
          </div>
          <BannerDeeplinkField
            form={form}
            namePath={namePath}
            itemNamePath={itemNamePath}
            resultParamsPath={resultParamsPath}
            conditionsPath={conditionsPath}
            restrictionDisplayNames={restrictionDisplayNames}
          />
        </div>
      </section>
    </>
  );

  const hiddenDetailsField = (
    <Form.Item name={itemPath("details", "text", "en")} hidden>
      <Input />
    </Form.Item>
  );

  const uploadModal = (
    <UploadImageModal
      open={isUploadOpen}
      onClose={() => setIsUploadOpen(false)}
      onUploaded={(imageUrl) =>
        form.setFieldValue([...namePath, "image", "url"], imageUrl)
      }
    />
  );
  const enabledUploadModal = enableWhenFormDisabled ? (
    <ConfigProvider componentDisabled={false}>{uploadModal}</ConfigProvider>
  ) : (
    uploadModal
  );

  const customizeModal = (
    <CustomizeBannerModal
      open={isCustomizeOpen}
      onClose={() => setIsCustomizeOpen(false)}
      form={form}
      namePath={namePath}
      itemNamePath={itemNamePath}
      selectedTheme={selectedTheme}
      onThemeChange={applyTheme}
      onEditImage={() => setIsUploadOpen(true)}
      enableWhenFormDisabled={enableWhenFormDisabled}
    />
  );

  if (children) {
    return (
      <>
        {hiddenDetailsField}
        {children({ formFields: bannerFormFields, previewColumn })}
        {enabledUploadModal}
        {customizeModal}
      </>
    );
  }

  return (
    <div className={styles.bannerEditor}>
      {hiddenDetailsField}
      <div className={styles.bannerEditorColumn}>{bannerFormFields}</div>
      {previewColumn}
      {enabledUploadModal}
      {customizeModal}
    </div>
  );
};

export default StepBannerEditor;
